/**
 * Thin wrapper around `cordova-plugin-purchase` (the Cordova/Capacitor IAP
 * plugin). The rest of the app talks to this module via async functions and
 * never touches `CdvPurchase.*` directly. The wrapper:
 *
 *   - registers the two products configured in the App Store / Play Console,
 *   - holds the single `store` instance,
 *   - fires backend verification on every `approved` transaction,
 *   - exposes promise-based `purchase()` and `restorePurchases()` calls.
 *
 * Web / non-native: every entry point throws `IapNotAvailableError`. The
 * paywall UI guards on `isNative` before calling in.
 *
 * `initIap()` runs at app start (PurchaseDialog mounts globally and fetches
 * prices) and must stay silent: no payment sheet, no Apple Account prompt, no
 * Play recovery card. Only `purchase()` and `restorePurchases()`, both behind a
 * tap, may reach UI. Policy in CLAUDE.md, "No payment-system UI at launch,
 * ever".
 *
 * The plugin is loaded by the cordova bridge at runtime; we treat
 * `window.CdvPurchase` as ambient. We do *not* import from the package
 * directly because its types use `declare namespace CdvPurchase` — they
 * arrive via the plugin's d.ts include, not as an ES module.
 */

import { isAndroid, isIOS, isNative } from './platform'
import {
  verifyPurchase as backendVerify,
  restorePurchases as backendRestore,
  type EntitlementResponse,
} from './api'

// --- Product catalog ------------------------------------------------------

export const PRODUCT_LIFETIME = 'lingotalk_lifetime'
export const PRODUCT_MONTHLY = 'lingotalk_monthly'
export const PRODUCT_IDS = [PRODUCT_LIFETIME, PRODUCT_MONTHLY] as const
export type ProductId = (typeof PRODUCT_IDS)[number]

export type IapProductInfo = {
  id: ProductId
  /** Localized display title from the store, or null until loaded. */
  title: string | null
  /** Localized formatted price (e.g. "14,99 €"), or null until loaded. */
  price: string | null
  /**
   * Integer price in 1/1,000,000 of the currency unit. Apple/Google quote
   * prices this way so amounts stay precise across currencies (no float
   * rounding around, e.g., ¥). 14990000 = €14.99.
   * Null until the store catalog is loaded.
   */
  priceMicros: number | null
  /** ISO 4217 currency code (e.g. "EUR"), or null until loaded. */
  currency: string | null
  /** 'subscription' | 'non_consumable' */
  kind: 'subscription' | 'non_consumable'
}

export class IapNotAvailableError extends Error {
  constructor() {
    super('IAP is not available on this platform.')
    this.name = 'IapNotAvailableError'
  }
}

export class IapPurchaseCancelledError extends Error {
  constructor() {
    super('Purchase cancelled by user.')
    this.name = 'IapPurchaseCancelledError'
  }
}

// --- Internal state -------------------------------------------------------

type PendingResolver = {
  resolve: (entitlement: unknown) => void
  reject: (err: Error) => void
}

let initPromise: Promise<void> | null = null
/** Set once `store.register()` has been called; the plugin cannot be re-initialized after that. */
let storeWired = false
const pendingByProduct = new Map<string, PendingResolver[]>()

/** How long we wait for the plugin to publish its namespace before giving up. */
const PLUGIN_LOAD_TIMEOUT_MS = 5000
const PLUGIN_POLL_INTERVAL_MS = 50

/**
 * The plugin publishes `window.CdvPurchase` in two steps, and only the second
 * one is usable.
 *
 * Capacitor's cordova shim maps every plugin's `clobbers` target onto `window`
 * as soon as the plugin file has been evaluated. `cordova-plugin-purchase`
 * never sets `module.exports`, so what lands on `window.CdvPurchase` at that
 * point is an **empty object**. The plugin fills in the real namespace
 * (`Platform`, `ProductType`, `store`, …) one macrotask later, from a
 * `setTimeout(initCDVPurchase, 0)` it schedules because `window.cordova` is
 * already defined.
 *
 * A truthiness check on the global therefore passes during that gap, and the
 * first property read — `cdv.Platform.GOOGLE_PLAY` — throws
 * "Cannot read properties of undefined (reading 'GOOGLE_PLAY')". That is the
 * production bug from 1.2.0 (seen in TelemetryDeck as `purchase.failed` with
 * errorCode `TypeError`). So: ready means the namespace is populated, not
 * merely present.
 */
function readyCdv(): CdvPurchaseGlobal | null {
  const cdv = (globalThis as unknown as { CdvPurchase?: Partial<CdvPurchaseGlobal> }).CdvPurchase
  if (!cdv || typeof cdv !== 'object') return null
  if (!cdv.store || !cdv.Platform || !cdv.ProductType || !cdv.ErrorCode) return null
  return cdv as CdvPurchaseGlobal
}

/**
 * Initialize the store exactly once per process. Idempotent — concurrent and
 * repeated calls share one in-flight promise. Resolves when the plugin is
 * ready to enumerate products.
 *
 * Failing to *find* the plugin is not memoized: it may simply not have been
 * ready yet (see `readyCdv`), and the next tap on "Kaufen" must get a fresh
 * attempt rather than the cached rejection from launch. Once the store has
 * been wired up the result sticks, because the plugin itself refuses a second
 * `store.initialize()`.
 */
export function initIap(): Promise<void> {
  if (!isNative) {
    return Promise.reject(new IapNotAvailableError())
  }
  if (initPromise !== null) return initPromise

  const attempt: Promise<void> = waitForPlugin()
    .then((cdv) => wireStore(cdv))
    .catch((e: unknown) => {
      if (!storeWired && initPromise === attempt) initPromise = null
      throw e
    })

  initPromise = attempt
  return attempt
}

/** Polls until the plugin has published a usable namespace, or gives up. */
function waitForPlugin(): Promise<CdvPurchaseGlobal> {
  return new Promise<CdvPurchaseGlobal>((resolve, reject) => {
    const start = Date.now()
    const poll = () => {
      // Inside a timer callback nobody is there to catch a throw: it would
      // surface as an uncaught error and leave this promise pending forever
      // (a spinner that never stops). Route anything unexpected into the
      // rejection instead.
      try {
        const cdv = readyCdv()
        if (cdv !== null) {
          resolve(cdv)
          return
        }
        if (Date.now() - start > PLUGIN_LOAD_TIMEOUT_MS) {
          reject(
            new Error(
              `cordova-plugin-purchase did not load within ${PLUGIN_LOAD_TIMEOUT_MS / 1000}s`,
            ),
          )
          return
        }
        setTimeout(poll, PLUGIN_POLL_INTERVAL_MS)
      } catch (e) {
        reject(e instanceof Error ? e : new Error(String(e)))
      }
    }
    poll()
  })
}

/** Registers our products, attaches the backend hook, and initializes the store. */
async function wireStore(cdv: CdvPurchaseGlobal): Promise<void> {
  const platform = currentStorePlatform(cdv)
  if (platform === null) {
    throw new IapNotAvailableError()
  }

  const { store, ProductType, Platform } = cdv

  storeWired = true
  store.register([
    { id: PRODUCT_LIFETIME, type: ProductType.NON_CONSUMABLE, platform },
    { id: PRODUCT_MONTHLY, type: ProductType.PAID_SUBSCRIPTION, platform },
  ])

  // Hook every approved transaction into our backend. The plugin emits
  // `approved` once Apple/Google have confirmed the buy; we then ask
  // our server to authoritatively validate, persist, and update the
  // patient's entitlement. Only on success do we finish() — finishing
  // commits Apple/Google and we lose the chance to retry validation.
  store.when().approved(async (transaction) => {
    try {
      await onApproved(transaction, platform === Platform.APPLE_APPSTORE ? 'ios' : 'android')
      await transaction.finish()
    } catch (err) {
      // Don't finish — the plugin will redeliver on next launch.
      rejectPendingForTransaction(transaction, err instanceof Error ? err : new Error(String(err)))
    }
  })

  await store.initialize([platform])
}

/**
 * Snapshot of currently-loaded products. Returns localized price strings
 * once the store has fetched them; before that, `price` and `title` are null.
 */
export async function getProducts(): Promise<IapProductInfo[]> {
  await initIap()
  const cdv = requireCdv()
  return PRODUCT_IDS.map((id) => {
    const product = cdv.store.get(id)
    const offer = product?.getOffer?.()
    const pricing = offer?.pricingPhases?.[0]
    return {
      id,
      title: product?.title ?? null,
      price: pricing?.price ?? null,
      priceMicros: pricing?.priceMicros ?? null,
      currency: pricing?.currency ?? null,
      kind: id === PRODUCT_LIFETIME ? 'non_consumable' : 'subscription',
    }
  })
}

/**
 * Initiate a purchase for the given product. Resolves once the transaction
 * has been verified by our backend and finished with the store. Rejects on
 * cancellation, store error, or backend verification failure.
 */
export async function purchase(productId: ProductId): Promise<void> {
  await initIap()
  const cdv = requireCdv()
  const product = cdv.store.get(productId)
  if (!product) {
    throw new Error(`Product ${productId} not found in store.`)
  }
  const offer = product.getOffer?.()
  if (!offer) {
    throw new Error(`Product ${productId} has no offer to order.`)
  }

  const completion = registerPending(productId)
  const orderError = await offer.order()
  if (orderError) {
    unregisterPending(productId, completion)
    if (isCancelled(cdv, orderError)) throw new IapPurchaseCancelledError()
    throw new Error(`Order failed: ${orderError.message ?? 'unknown'}`)
  }

  await completion.promise
}

/**
 * Restore previously-purchased non-consumables and active subscriptions for
 * the user's current store account (Apple ID / Google account). The plugin
 * re-emits `approved` for each found transaction; our listener forwards
 * them to the backend's restore endpoint.
 *
 * For v1 we let each transaction flow through `verify` independently. The
 * backend's restore endpoint exists for batch use; we don't need it from
 * the cordova-plugin-purchase flow because the plugin replays transactions
 * one by one, and our `verify` endpoint is idempotent (UNIQUE on
 * transaction_id).
 */
export async function restorePurchases(): Promise<void> {
  await initIap()
  const cdv = requireCdv()
  await cdv.store.restorePurchases()
}

// --- internals ------------------------------------------------------------

/**
 * Notified whenever a transaction has been verified against our backend.
 *
 * This matters most for the transactions nobody asked for: when the plugin
 * initializes, StoreKit replays every transaction we have not finished — an
 * auto-renewal among them — straight to the observer. Those go through
 * `onApproved` below and update the patient row on the server, but nothing in
 * the app would otherwise hear about it. One subscriber (the entitlement
 * store) registers here so a renewal lands in the UI as soon as it is
 * verified, instead of waiting for whatever refresh happens next.
 */
type VerifiedListener = (entitlement: EntitlementResponse) => void

let verifiedListener: VerifiedListener | null = null

export function onEntitlementVerified(listener: VerifiedListener | null): void {
  verifiedListener = listener
}

async function onApproved(transaction: CdvTransaction, platform: 'ios' | 'android'): Promise<void> {
  const productId = (transaction.products?.[0]?.id ?? null) as ProductId | null
  if (productId === null) {
    throw new Error('Approved transaction had no product id.')
  }

  const entitlement = await backendVerify({
    platform,
    product_id: productId,
    transaction_id: transaction.transactionId,
    jws_or_token:
      // iOS StoreKit 2 exposes the JWS in transaction.purchaseToken; Android
      // uses purchaseToken for the Play purchase token. Pass either through.
      transaction.purchaseToken ?? null,
  })

  // The server has already applied this; tell whoever is listening so the UI
  // does not have to go and ask again.
  verifiedListener?.(entitlement)

  resolvePendingFor(productId)
}

type Pending = { promise: Promise<unknown>; resolve: (v: unknown) => void; reject: (e: Error) => void }
function registerPending(productId: ProductId): Pending {
  let resolve!: (v: unknown) => void
  let reject!: (e: Error) => void
  const promise = new Promise<unknown>((res, rej) => {
    resolve = res
    reject = rej
  })
  const list = pendingByProduct.get(productId) ?? []
  list.push({ resolve, reject })
  pendingByProduct.set(productId, list)
  return { promise, resolve, reject }
}
function unregisterPending(productId: ProductId, p: Pending): void {
  const list = pendingByProduct.get(productId)
  if (!list) return
  const idx = list.findIndex((entry) => entry.resolve === p.resolve)
  if (idx >= 0) list.splice(idx, 1)
}
function resolvePendingFor(productId: string): void {
  const list = pendingByProduct.get(productId)
  if (!list || list.length === 0) return
  for (const { resolve } of list) resolve(undefined)
  pendingByProduct.set(productId, [])
}
function rejectPendingForTransaction(transaction: CdvTransaction, err: Error): void {
  const productId = transaction.products?.[0]?.id
  if (!productId) return
  const list = pendingByProduct.get(productId)
  if (!list || list.length === 0) return
  for (const { reject } of list) reject(err)
  pendingByProduct.set(productId, [])
}

function currentStorePlatform(cdv: CdvPurchaseGlobal): number | null {
  if (isIOS) return cdv.Platform.APPLE_APPSTORE
  if (isAndroid) return cdv.Platform.GOOGLE_PLAY
  return null
}

function requireCdv(): CdvPurchaseGlobal {
  const cdv = readyCdv()
  if (cdv === null) throw new IapNotAvailableError()
  return cdv
}

function isCancelled(cdv: CdvPurchaseGlobal, err: { code?: number }): boolean {
  return err.code === cdv.ErrorCode.PAYMENT_CANCELLED
}

// --- ambient typing of the plugin's globals (kept narrow on purpose) ------

type CdvTransaction = {
  transactionId: string
  purchaseToken?: string | null
  products?: Array<{ id: string }>
  finish(): Promise<void>
}

type CdvPurchaseGlobal = {
  store: {
    register(products: Array<{ id: string; type: number; platform: number }>): void
    initialize(platforms: number[]): Promise<void>
    when(): {
      approved(handler: (t: CdvTransaction) => void | Promise<void>): unknown
    }
    get(id: string):
      | {
          title?: string
          getOffer?: () => {
            pricingPhases?: Array<{
              price?: string
              /** Integer price in micros — Apple/Google quote this so
               *  cross-currency math stays precise. */
              priceMicros?: number
              /** ISO 4217 currency code, e.g. "EUR". */
              currency?: string
            }>
            order(): Promise<{ code?: number; message?: string } | undefined>
          }
        }
      | undefined
    restorePurchases(): Promise<void>
  }
  Platform: { APPLE_APPSTORE: number; GOOGLE_PLAY: number }
  ProductType: { NON_CONSUMABLE: number; PAID_SUBSCRIPTION: number }
  ErrorCode: { PAYMENT_CANCELLED: number }
}

// We import the api `restorePurchases` only to keep the symbol live for
// future batch use; v1's flow goes through verify() per-transaction.
void backendRestore
